import { useQuery } from '@tanstack/react-query'
import {
  Users,
  UserCheck,
  Phone,
  Mail,
  TrendingUp,
  Search,
  Clock,
  Activity,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { MetricCard } from '@/components/common/MetricCard'
import { StatusBadge } from '@/components/common/StatusBadge'
import { PageLoader } from '@/components/common/LoadingSpinner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { dashboardApi } from '@/api/endpoints/dashboard'
import { formatRelativeTime } from '@/lib/utils'

export function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardApi.getStats().then((res) => res.data),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const { data: recentLeadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['dashboard', 'recent-leads'],
    queryFn: () => dashboardApi.getRecentLeads(5).then((res) => res.data),
    refetchInterval: 30000,
  })

  const { data: recentActivitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ['dashboard', 'recent-activities'],
    queryFn: () => dashboardApi.getRecentActivities(10).then((res) => res.data),
    refetchInterval: 30000,
  })

  // Extract arrays from response objects
  const recentLeads = recentLeadsData?.leads || []
  const recentActivities = recentActivitiesData?.activities || []

  const { data: agents } = useQuery({
    queryKey: ['dashboard', 'agents'],
    queryFn: () => dashboardApi.getAgentStatus().then((res) => res.data),
    refetchInterval: 10000,
  })

  if (statsLoading) {
    return <PageLoader />
  }

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Leads"
          value={stats?.total_leads?.toLocaleString() || '0'}
          icon={Users}
          gradient="blue"
        />
        <MetricCard
          title="Active Leads"
          value={stats?.active_leads?.toLocaleString() || '0'}
          icon={UserCheck}
          gradient="green"
        />
        <MetricCard
          title="Total Calls"
          value={stats?.total_calls?.toLocaleString() || '0'}
          icon={Phone}
          gradient="purple"
        />
        <MetricCard
          title="Total Emails"
          value={stats?.total_emails?.toLocaleString() || '0'}
          icon={Mail}
          gradient="orange"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Conversion Rate"
          value={`${stats?.conversion_rate?.toFixed(1) || '0'}%`}
          icon={TrendingUp}
          gradient="cyan"
        />
        <MetricCard
          title="Scouting Activities"
          value={stats?.scouting_activities?.toLocaleString() || '0'}
          icon={Search}
          gradient="pink"
        />
        <MetricCard
          title="Avg Response Time"
          value={`${stats?.avg_response_time?.toFixed(1) || '0'}h`}
          icon={Clock}
          gradient="blue"
        />
        <MetricCard
          title="New Today"
          value={stats?.new_leads_today?.toLocaleString() || '0'}
          icon={Activity}
          gradient="green"
        />
      </div>

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Leads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Leads</CardTitle>
            <Link
              to="/leads"
              className="text-sm text-primary-600 hover:underline"
            >
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {leadsLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="h-12 animate-pulse rounded bg-gray-100"
                  />
                ))}
              </div>
            ) : recentLeads?.length ? (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {lead.company_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {lead.contact_name || 'No contact'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={lead.status} variant="lead" />
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(lead.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No recent leads</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Activities</CardTitle>
            <Link
              to="/activities"
              className="text-sm text-primary-600 hover:underline"
            >
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {activitiesLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="h-12 animate-pulse rounded bg-gray-100"
                  />
                ))}
              </div>
            ) : recentActivities?.length ? (
              <div className="space-y-3">
                {recentActivities.map((activity) => {
                  // Determine activity category for icon color
                  const isCall = activity.activity_type?.toLowerCase().includes('call')
                  const isEmail = activity.activity_type?.toLowerCase().includes('email')

                  // Build description from available fields
                  const description = activity.subject
                    || activity.notes
                    || `${activity.activity_type}${activity.outcome ? ` - ${activity.outcome}` : ''}`

                  // Get company name from details or metadata if available
                  const leadCompany = (activity.details as Record<string, string> | null)?.company_name
                    || (activity.metadata_ as Record<string, string> | null)?.company_name
                    || null

                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 rounded-lg border p-3"
                    >
                      <div
                        className={`mt-0.5 h-2 w-2 rounded-full ${
                          isCall
                            ? 'bg-green-500'
                            : isEmail
                            ? 'bg-blue-500'
                            : 'bg-gray-400'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {description}
                        </p>
                        {leadCompany && (
                          <p className="text-xs text-gray-500">
                            {leadCompany}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatRelativeTime(activity.created_at)}
                      </span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No recent activities
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agent Status */}
      {agents && agents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">AI Agents Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {agents.map((agent) => (
                <div
                  key={agent.name}
                  className="flex items-center gap-3 rounded-lg border p-4"
                >
                  <div
                    className={`h-3 w-3 rounded-full ${
                      agent.status === 'active'
                        ? 'bg-green-500'
                        : agent.status === 'idle'
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  />
                  <div>
                    <p className="font-medium text-gray-900">{agent.name}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      {agent.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
